import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import Footer from "@/components/Footer";
import { Search, SlidersHorizontal, Loader2, X } from "lucide-react";

const CATEGORIES = ["Rides", "Tours", "Activities", "Transfers"];

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [islands, setIslands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");

  const island = params.get("island") || "";
  const category = params.get("category") || "";
  const maxPrice = params.get("max_price") || "";
  const sort = params.get("sort") || "recommended";

  useEffect(() => {
    api.get("/meta").then((r) => setIslands(r.data.islands)).catch(() => {});
  }, []);

  const fetchListings = useCallback(() => {
    setLoading(true);
    const p = {};
    if (island) p.island = island;
    if (category) p.category = category;
    if (maxPrice) p.max_price = maxPrice;
    if (params.get("q")) p.q = params.get("q");
    if (sort) p.sort = sort;
    api.get("/listings", { params: p })
      .then((r) => setListings(r.data))
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  }, [island, category, maxPrice, sort, params]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    update("q", q);
  };

  const clearAll = () => { setQ(""); setParams(new URLSearchParams()); };
  const activeFilters = [island, category, maxPrice, params.get("q")].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#060810]">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-10 pb-6">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
          {category || "Explore"} <span className="text-gradient-neon">{island && `· ${island}`}</span>
        </h1>
        <p className="text-slate-400 mt-2 text-sm">{loading ? "Searching…" : `${listings.length} extraordinary options`}</p>

        {/* Filter bar */}
        <div className="glass rounded-2xl p-4 mt-6 flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
          <form onSubmit={submitSearch} className="flex-1 flex items-center gap-3 px-4 py-2.5 rounded-xl bg-[#0B0E1B] border border-[#1D2640]">
            <Search className="w-5 h-5 text-cyan-400 shrink-0" />
            <input
              data-testid="browse-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search tours, rides, islands…"
              className="bg-transparent text-white text-sm w-full outline-none placeholder:text-slate-500"
            />
          </form>

          <select data-testid="filter-island-select" value={island} onChange={(e) => update("island", e.target.value)}
            className="bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-2.5 text-sm text-white outline-none cursor-pointer">
            <option value="">All islands</option>
            {islands.map((i) => <option key={i} value={i} className="bg-[#0B0E1B]">{i}</option>)}
          </select>

          <select data-testid="filter-category-select" value={category} onChange={(e) => update("category", e.target.value)}
            className="bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-2.5 text-sm text-white outline-none cursor-pointer">
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c} className="bg-[#0B0E1B]">{c}</option>)}
          </select>

          <select data-testid="filter-sort-select" value={sort} onChange={(e) => update("sort", e.target.value)}
            className="bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-2.5 text-sm text-white outline-none cursor-pointer">
            <option value="recommended">Recommended</option>
            <option value="price_asc">Price: low to high</option>
            <option value="price_desc">Price: high to low</option>
            <option value="rating">Top rated</option>
          </select>

          {activeFilters > 0 && (
            <button onClick={clearAll} data-testid="clear-filters-btn"
              className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#FF5E7E] px-3 py-2.5">
              <X className="w-4 h-4" /> Clear
            </button>
          )}
        </div>

        {/* Price quick filter */}
        <div className="flex items-center gap-3 mt-4 overflow-x-auto no-scrollbar">
          <span className="flex items-center gap-1.5 text-xs text-slate-500 shrink-0">
            <SlidersHorizontal className="w-3.5 h-3.5" /> Max price:
          </span>
          {[300, 500, 1000, 2000].map((price) => (
            <button
              key={price}
              onClick={() => update("max_price", String(maxPrice) === String(price) ? "" : String(price))}
              data-testid={`price-chip-${price}`}
              className={`text-xs px-4 py-1.5 rounded-full border shrink-0 transition-colors ${
                String(maxPrice) === String(price)
                  ? "btn-neon border-transparent font-semibold"
                  : "glass border-[#1D2640] text-slate-300 hover:border-cyan-400/40"
              }`}
            >
              ${price.toLocaleString()}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-16">
        {loading ? (
          <div className="flex justify-center py-32">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-32">
            <p className="font-heading text-xl text-white mb-2">No results found</p>
            <p className="text-slate-400 text-sm mb-6">Try widening your filters.</p>
            <button onClick={clearAll} className="btn-neon rounded-full px-6 py-2.5 font-semibold text-sm">Reset filters</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {listings.map((l) => <ListingCard key={l.id} listing={l} />)}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
